import { z } from 'zod';
import { financeReportFilterSchema } from './filters.js';

/** Saved and custom report configurations, and scheduled delivery. None of these count among the 81 reports. */

export const REPORT_EXPORT_FORMAT_VALUES = ['csv', 'xlsx', 'pdf'] as const;
export type ReportExportFormatValue = (typeof REPORT_EXPORT_FORMAT_VALUES)[number];

export const REPORT_SCHEDULE_FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'] as const;

const reportId = z.string().trim().min(1).max(80);
const filtersObject = financeReportFilterSchema.partial();

export const createReportConfigSchema = z.object({
  reportId,
  name: z.string().trim().min(1, 'Required').max(150),
  description: z.string().trim().max(1000).nullish(),
  filters: filtersObject.default({}),
  /** Visible columns, in order. Empty means the report’s own default. */
  columns: z.array(z.string().trim().min(1).max(60)).max(40).default([]),
  sortBy: z.string().trim().min(1).max(60).nullish(),
  sortDir: z.enum(['asc', 'desc']).nullish(),
  visibility: z.enum(['PRIVATE', 'SHARED']).default('PRIVATE'),
});
export type CreateReportConfigInput = z.infer<typeof createReportConfigSchema>;

export const updateReportConfigSchema = createReportConfigSchema.omit({ reportId: true }).partial();
export type UpdateReportConfigInput = z.infer<typeof updateReportConfigSchema>;

export const listReportConfigsQuerySchema = z.object({ reportId: reportId.optional() });

export const cloneReportConfigSchema = z.object({ name: z.string().trim().min(1).max(150).optional() });

export interface ReportConfigView {
  id: string;
  reportId: string;
  name: string;
  description: string | null;
  filters: Record<string, unknown>;
  columns: string[];
  sortBy: string | null;
  sortDir: 'asc' | 'desc' | null;
  visibility: 'PRIVATE' | 'SHARED';
  ownerId: string;
  ownerName: string;
  mine: boolean;
  createdAt: string;
  updatedAt: string;
}

export const createReportScheduleSchema = z.object({
  reportId,
  configId: z.uuid().nullish(),
  filters: filtersObject.default({}),
  format: z.enum(REPORT_EXPORT_FORMAT_VALUES).default('pdf'),
  frequency: z.enum(REPORT_SCHEDULE_FREQUENCIES),
  /** Internal users only. */
  recipientIds: z.array(z.uuid()).min(1, 'Choose at least one recipient').max(25),
  /** First delivery. Defaults to tomorrow. */
  startOn: z.iso.date().optional(),
});
export type CreateReportScheduleInput = z.infer<typeof createReportScheduleSchema>;

export const updateReportScheduleSchema = z.object({
  filters: filtersObject.optional(),
  format: z.enum(REPORT_EXPORT_FORMAT_VALUES).optional(),
  frequency: z.enum(REPORT_SCHEDULE_FREQUENCIES).optional(),
  recipientIds: z.array(z.uuid()).min(1).max(25).optional(),
  enabled: z.boolean().optional(),
  nextRunOn: z.iso.date().optional(),
});
export type UpdateReportScheduleInput = z.infer<typeof updateReportScheduleSchema>;

export interface ReportScheduleView {
  id: string;
  reportId: string;
  reportName: string;
  configId: string | null;
  filters: Record<string, unknown>;
  format: ReportExportFormatValue;
  frequency: (typeof REPORT_SCHEDULE_FREQUENCIES)[number];
  recipients: { id: string; name: string }[];
  nextRunAt: string;
  enabled: boolean;
  lastRunAt: string | null;
  lastStatus: 'SUCCEEDED' | 'FAILED' | null;
  lastError: string | null;
  ownerId: string;
  mine: boolean;
}

export interface ReportDeliveryView {
  id: string;
  scheduleId: string;
  reportId: string;
  reportName: string;
  status: 'SUCCEEDED' | 'FAILED';
  error: string | null;
  fileName: string | null;
  createdAt: string;
}
